import { useCallback, useEffect, useRef, useState } from "react";
import { appConfirm } from "../../components/ConfirmDialog";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Mail, FileText, Bell, Upload, Trash2, Check, Loader2, ArrowDownLeft, ArrowUpRight, Plus } from "lucide-react";
import { QuoteFollowUpEmail } from "./QuoteFollowUpEmail";

const dateTimeGB = (d) => d ? new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function QuoteComms({ quoteId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [remForm, setRemForm] = useState({ title: "", due_date: "" });
  const fileRef = useRef(null);

  const load = useCallback(() => api.get(`/admin/quote/${encodeURIComponent(quoteId)}/comms`)
    .then(r => setData(r.data)).catch(() => toast.error("Could not load comms"))
    .finally(() => setLoading(false)), [quoteId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/quote/${encodeURIComponent(quoteId)}/document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded — it will carry over if the job is confirmed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const delDoc = async (id) => {
    if (!(await appConfirm({ title: "Delete this document?", confirmLabel: "Delete", destructive: true }))) return;
    try { await api.delete(`/admin/document/${id}`); load(); }
    catch { toast.error("Could not delete"); }
  };

  const addReminder = async () => {
    if (!remForm.title.trim()) { toast.error("Add a reminder title"); return; }
    try {
      await api.post("/admin/reminders", { ...remForm, reference: data?.reference || "", quote_id: quoteId });
      toast.success("Reminder added — it shows on the dashboard too");
      setRemForm({ title: "", due_date: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add reminder"); }
  };

  const toggleReminder = async (r) => {
    await api.patch(`/admin/reminders/${r.id}`, { status: r.status === "open" ? "done" : "open" });
    load();
  };
  const delReminder = async (id) => { await api.delete(`/admin/reminders/${id}`); load(); };

  if (loading) return <div className="py-8 text-center text-sm text-[#6B7280]"><Loader2 size={14} className="animate-spin inline mr-2" /> Loading…</div>;
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="quote-comms">
      <div className="border border-[#E5E7EB] bg-white p-4" data-testid="quote-comms-reminders">
        <div className="label-eyebrow mb-3 flex items-center gap-1.5"><Bell size={13} /> Reminders</div>
        <div className="flex gap-2 mb-3">
          <input data-testid="quote-reminder-title" value={remForm.title} onChange={e => setRemForm(f => ({ ...f, title: e.target.value }))}
            placeholder="e.g. Call about the site visit" className="flex-1 border border-[#E5E7EB] px-3 py-2 text-sm" />
          <input data-testid="quote-reminder-due" type="date" value={remForm.due_date} onChange={e => setRemForm(f => ({ ...f, due_date: e.target.value }))}
            className="border border-[#E5E7EB] px-2 py-2 text-sm" />
          <button data-testid="quote-reminder-add" onClick={addReminder} className="de-btn-primary text-xs inline-flex items-center gap-1"><Plus size={13} /> Add</button>
        </div>
        {data.reminders.length === 0 ? (
          <div className="text-sm text-[#9CA3AF] italic">No reminders for this client. Reminders also appear on the Dashboard.</div>
        ) : (
          <ul className="space-y-1.5">
            {data.reminders.map(r => (
              <li key={r.id} className="flex items-center gap-2 border border-[#F0EAE0] px-3 py-2" data-testid={`quote-reminder-${r.id}`}>
                <button data-testid={`quote-reminder-toggle-${r.id}`} onClick={() => toggleReminder(r)}
                  className={`w-4 h-4 border flex items-center justify-center shrink-0 ${r.status === "done" ? "bg-[#C79A3B] border-[#C79A3B]" : "border-[#12263A] bg-white"}`}>
                  {r.status === "done" && <Check size={11} className="text-white" />}
                </button>
                <span className={`text-sm flex-1 ${r.status === "done" ? "line-through text-[#6B7280]" : "text-[#12263A]"}`}>{r.title}</span>
                {r.due_date && <span className="text-[11px] text-[#6B7280]">{new Date(r.due_date).toLocaleDateString("en-GB")}</span>}
                <button data-testid={`quote-reminder-delete-${r.id}`} onClick={() => delReminder(r.id)} className="text-[#6B7280] hover:text-[#C0392B]"><Trash2 size={13} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border border-[#E5E7EB] bg-white p-4" data-testid="quote-comms-documents">
        <div className="flex items-center justify-between mb-3">
          <div className="label-eyebrow flex items-center gap-1.5"><FileText size={13} /> Documents</div>
          <label className={`inline-flex items-center gap-1.5 text-xs font-bold border border-[#E5E7EB] px-2.5 py-1.5 cursor-pointer hover:border-[#C79A3B] ${uploading ? "opacity-50" : ""}`}>
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload
            <input ref={fileRef} data-testid="quote-doc-upload" type="file" className="hidden" disabled={uploading}
              onChange={(e) => upload(e.target.files?.[0])} />
          </label>
        </div>
        {data.documents.length === 0 ? (
          <div className="text-sm text-[#9CA3AF] italic">No documents on file. Anything uploaded here carries over to the Job File automatically when the job is confirmed.</div>
        ) : (
          <ul className="divide-y divide-[#F0EAE0]">
            {data.documents.map(d => (
              <li key={d.id} className="py-2 flex items-center gap-2" data-testid={`quote-doc-${d.id}`}>
                <FileText size={14} className="text-[#6B7280] shrink-0" />
                <a href={`${BACKEND}/api/admin/document/${d.id}`} target="_blank" rel="noreferrer" className="text-sm font-semibold de-link truncate flex-1">{d.filename}</a>
                <span className="text-[11px] text-[#6B7280] shrink-0">{dateTimeGB(d.created_at)}</span>
                {!d.system && (
                  <button data-testid={`quote-doc-delete-${d.id}`} onClick={() => delDoc(d.id)} className="text-[#6B7280] hover:text-[#C0392B]"><Trash2 size={13} /></button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border border-[#E5E7EB] bg-white p-4" data-testid="quote-comms-emails">
        <div className="flex items-center justify-between mb-3">
          <div className="label-eyebrow flex items-center gap-1.5"><Mail size={13} /> Emails {data.email ? `— ${data.email}` : ""}</div>
          <QuoteFollowUpEmail quoteId={quoteId} onSent={load} />
        </div>
        {data.followup && (
          <div className="mb-3 text-[11px] border border-[#F0EAE0] bg-[#F8F5F0] px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="quote-followup-status">
            <span className="text-[#6B7280]">Follow-up sent {dateTimeGB(data.followup.sent_at)}{data.followup.sent_by ? ` by ${data.followup.sent_by}` : ""}</span>
            {data.followup.opened_at
              ? <span className="font-black uppercase tracking-widest text-[9px] px-1.5 py-0.5 bg-[#E3EDF7] text-[#2E6BB0] border border-[#C4D9EE] rounded-sm">Opened ✓ {dateTimeGB(data.followup.opened_at)}{data.followup.open_count > 1 ? ` · ×${data.followup.open_count}` : ""}</span>
              : <span className="font-black uppercase tracking-widest text-[9px] px-1.5 py-0.5 bg-[#F4F4F4] text-[#6B7280] border border-[#E5E7EB] rounded-sm">Not opened yet</span>}
          </div>
        )}
        {data.emails.length === 0 ? (
          <div className="text-sm text-[#9CA3AF] italic">No emails logged for this client yet — automated quote emails, nudges and Gmail replies appear here.</div>
        ) : (
          <ul className="divide-y divide-[#F0EAE0]">
            {data.emails.map(e => (
              <li key={e.id} className="py-2" data-testid={`quote-email-${e.id}`}>
                <div className="flex items-start gap-2">
                  {e.direction === "inbound"
                    ? <ArrowDownLeft size={14} className="mt-0.5 text-[#1F6B3B] shrink-0" />
                    : <ArrowUpRight size={14} className="mt-0.5 text-[#2E6BB0] shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-[#12263A] truncate">{e.subject}</div>
                    <div className="text-[11px] text-[#6B7280] mt-0.5">
                      {e.direction === "inbound" ? "Received" : "Sent"} · {dateTimeGB(e.created_at)}
                      {e.kind ? ` · ${e.kind}` : ""}{e.status === "failed" ? " · " : ""}
                      {e.status === "failed" && <b className="text-[#C0392B]">failed</b>}
                    </div>
                    {e.body && (
                      <details className="mt-1">
                        <summary className="text-[11px] font-bold text-[#C79A3B] cursor-pointer">View message</summary>
                        <p className="text-xs text-[#374151] whitespace-pre-wrap mt-1 bg-[#F8F5F0] p-2 border border-[#F0EAE0]">{e.body}</p>
                      </details>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
